import React from 'react'
import { Link } from 'react-router-dom'
import { Target } from 'lucide-react'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet'
import { toast } from '@/components/ui/sonner'
import { cn } from '@/lib/utils'
import { useCreatePlannedPostMutation, useGetPlannerWeekQuery } from '@/redux/collaborationsApi'
import { fmt, localToday, mondayOf, newDraft } from './postHelpers'
import { PostForm } from './PostForm'

/**
 * The weekly posting goal in one line, for the top of the calendar: posted so far, what hitting or
 * missing it means, and the last four finished weeks.
 */
export const PostingGoalStrip: React.FC = () => {
  const { data } = useGetPlannerWeekQuery(mondayOf(localToday()))
  const summary = data?.summary
  if (!summary) return null
  const { goal, thisWeek } = summary
  const hit = thisWeek >= goal
  return (
    <div className="rounded-2xl border border-border bg-card px-4 py-3 shadow-sm">
      <div className="flex items-center gap-3">
        <span
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
            hit ? 'bg-[#C0FD72]' : 'bg-[#E8F9D2]',
          )}
        >
          <Target className="h-5 w-5 text-[#0F172A]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-foreground tabular-nums">
            {thisWeek} of {goal} posts this week
          </p>
          <p className="text-xs text-muted-foreground">
            {hit
              ? 'Goal hit. Brands notice athletes who post consistently.'
              : `${goal - thisWeek} more by Sunday keeps your content consistent.`}
          </p>
        </div>
        <div className="flex shrink-0 gap-1" aria-label="Last four weeks">
          {summary.weeks
            .slice(0, 4)
            .reverse()
            .map((w) => (
              <span
                key={w.weekStart}
                title={`Week of ${fmt(w.weekStart, { month: 'short', day: 'numeric' })}: ${w.posted} posted`}
                className={cn(
                  'h-2.5 w-2.5 rounded-full',
                  w.posted >= goal ? 'bg-[#84CC16]' : 'bg-[#CBD5E1]',
                )}
              />
            ))}
        </div>
      </div>
      <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-[#84CC16] transition-all"
          style={{ width: `${Math.min(100, (thisWeek / goal) * 100)}%` }}
        />
      </div>
    </div>
  )
}

/** "+ Add post" on the calendar: the post form in a sheet, for the chosen day */
export const AddPostSheet: React.FC<{
  day: string | null
  onClose: () => void
  onChanged?: () => void
}> = ({ day, onClose, onChanged }) => {
  const plannedDay = day && day >= localToday() ? day : localToday()
  const { data } = useGetPlannerWeekQuery(mondayOf(plannedDay), { skip: !day })
  const [create, { isLoading }] = useCreatePlannedPostMutation()
  const themes = data?.themes ?? []
  return (
    <Sheet open={!!day} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="bottom"
        className="mx-auto max-h-[90dvh] max-w-lg overflow-y-auto rounded-t-3xl p-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
      >
        <SheetTitle className="text-lg font-bold text-foreground">Plan a post</SheetTitle>
        <SheetDescription className="text-sm text-muted-foreground">
          Two a week keeps your content consistent.
          {!themes.length ? (
            <>
              {' '}
              <Link to="/setup/themes" className="font-semibold text-[#3F7A0A] underline">
                Add your content themes
              </Link>{' '}
              to plan around them.
            </>
          ) : null}
        </SheetDescription>
        {day && data ? (
          <PostForm
            key={plannedDay}
            initial={newDraft(plannedDay, themes)}
            themes={themes}
            saving={isLoading}
            submitLabel="Plan it"
            onCancel={onClose}
            onSubmit={async (d) => {
              try {
                await create({
                  plannedOn: d.plannedOn,
                  plannedTime: d.plannedTime || null,
                  platform: d.platform,
                  theme: d.theme,
                  idea: d.idea.trim() || null,
                }).unwrap()
                toast('Post planned', {
                  description: `${fmt(d.plannedOn, { weekday: 'long', month: 'short', day: 'numeric' })}. We'll remind you that morning.`,
                })
                onChanged?.()
                onClose()
              } catch (err: any) {
                toast.error('That didn’t save', { description: err?.data?.error ?? err?.message })
              }
            }}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  )
}
